/**
 * Channel detail — ad-service network mapping, static chrome, and the pure mappers
 * that turn a Casino Ad Service campaign + its today figures into the card model.
 *
 * The channel pages list the active brand's REAL campaigns (filtered to the channel's
 * `network`) instead of a shared mock pool. Each card carries the campaign's real
 * artwork / title / period / status and links to the real campaign-detail page; the
 * four stat tiles read today's Daily Spend (Ad Service `cost`) + Registrations / FTDs /
 * Avg FTD Value (Stats `daily_affiliate_activity`, `campaign` dimension). Kept pure (no
 * Redux / effects) so it unit-tests directly; the fetching lives in
 * {@link useChannelCampaigns}.
 */
import type { IconName } from '../../components/Icon/Icon';
import type { ChannelCampaign, ChannelId, ChannelMetric } from '../../mock-data/types';
import {
  type AdCampaignDetail,
  type AdCampaignNetwork,
  type AdCampaignSummary,
  resolveCampaignName,
  toCampaignStatus,
} from '../acquisition/api/casino-ad-service';
import { formatCampaignPeriod, pickThumbnail } from '../acquisition/campaign-mapping';
import { buildStat, markerText } from '../acquisition/performance-stats';
import type { Bucket } from '../home/stats-shape';
import { formatMoney } from '../../utils/money';

/**
 * Channel page id → the ad-service `network` literal its campaigns run on. Confirmed
 * `web` for web-networks (the only reachable channel today — the rest are gated behind
 * the release feature flag); app / crypto / video mirror the channel `aft` and light up
 * unchanged when those networks + channels unlock. See `config/acquisitionChannels`.
 */
export const CHANNEL_NETWORK: Record<ChannelId, AdCampaignNetwork> = {
  'web-networks': 'web',
  'app-networks': 'app',
  'crypto-sites': 'crypto',
  'short-videos': 'video',
};

/** Static per-channel chrome (header title, icon, summary name) — a fixed, closed UI
 *  vocabulary translated at the render site, NOT data. Replaces the deleted mock. */
export const CHANNEL_CHROME: Record<ChannelId, { title: string; icon: IconName; name: string }> = {
  'web-networks': { title: 'Web Networks', icon: 'globe', name: 'Web Networks' },
  'app-networks': { title: 'App Networks', icon: 'mobile', name: 'App Networks' },
  'crypto-sites': { title: 'Crypto Sites', icon: 'crypto', name: 'Crypto Sites' },
  'short-videos': { title: 'Short Videos', icon: 'video', name: 'Short Videos' },
};

/** "Today's Performance" metric scaffold (id + label only); the figures are overridden
 *  with the API-sourced ones in {@link buildChannelMetrics}. Same four for every channel. */
export const CHANNEL_METRIC_SCAFFOLD: ChannelMetric[] = [
  { id: 'registrations', label: 'Registrations', value: '' },
  { id: 'ftds', label: 'FTDs', value: '' },
  { id: 'avg-ftd-value', label: 'Avg FTD Value', value: '' },
  { id: 'daily-spend', label: 'Daily Spend', value: '' },
];

/** The top "Cost per Acquisition" card scaffold (id + label); the figure is built from
 *  the channel ad-spend ÷ FTDs in {@link buildChannelCpa}. */
export const CHANNEL_CPA_SCAFFOLD: ChannelMetric = { id: 'cpa', label: 'Cost per Acquisition', value: '' };

/**
 * The four per-campaign tile strings for one card, from today's Ad Service `cost` and
 * the campaign's today Stats buckets. The tiles carry no delta, so the baseline is empty
 * (the value is all we read). Daily Spend is the ad cost; Registrations / FTDs are counts;
 * Avg FTD Value is `ftd_total_amount / ftds` (`–` when zero FTDs). A failing stats request
 * reads `N/A` on the three stats tiles; a per-campaign cost failure already counts as `$0`.
 */
export const buildCampaignTileStats = (
  costToday: number,
  stats: Bucket[],
  statsErrored: boolean,
): ChannelCampaign['stats'] => ({
  dailySpend: formatMoney(Math.round(costToday)),
  registrations: markerText(buildStat({ kind: 'sum', fields: ['registrations'], money: false }, stats, [], statsErrored)),
  ftds: markerText(buildStat({ kind: 'sum', fields: ['ftds'], money: false }, stats, [], statsErrored)),
  ftdValue: markerText(
    buildStat({ kind: 'ratio', num: ['ftd_total_amount'], den: ['ftds'], money: true }, stats, [], statsErrored),
  ),
});

/**
 * Map one Ad Service campaign (+ its detail for the thumbnail + its built tile stats)
 * onto the channel-card {@link ChannelCampaign}, or `null` when the studio never shows
 * that campaign (`Failed` / `Deleted` / an unrecognised state) — the channel has no card
 * for it either. The card carries ONE real campaign id; the
 * detail page reflects its true running / paused status, so both the running and paused
 * detail ids are that same id. `enabled` seeds the card toggle from the real provider
 * status (Running → on).
 */
export const toChannelCampaign = (
  summary: AdCampaignSummary,
  detail: AdCampaignDetail | undefined,
  stats: ChannelCampaign['stats'],
): ChannelCampaign | null => {
  const status = toCampaignStatus(summary.status);
  if (status == null) return null;
  return {
    id: summary.campaign_id,
    runningDetailId: summary.campaign_id,
    pausedDetailId: summary.campaign_id,
    period: formatCampaignPeriod(summary.start_date, summary.end_date),
    title: resolveCampaignName(summary.campaign_name),
    enabled: status === 'running',
    // The campaign's master key art (on the list row); a generated banner only backstops it.
    image: summary.master_key_art || (detail ? pickThumbnail(detail.assets) : ''),
    stats,
  };
};
